import React from 'react';
import { Link } from 'react-router-dom';

// Renders a page body written in the small markup the Pages editor describes:
//   # Heading   ## Subheading   - bullet   blank line = new paragraph
//   **bold**   [text](https://... | mailto:... | /path)
// Everything becomes React elements - never HTML - so a page body cannot inject
// script, and links are limited to http(s), mailto and site paths.

const INLINE_RE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g;
const LINK_RE = /^\[([^\]]+)\]\(([^)\s]+)\)$/;
const safeHref = (href) => (/^(https?:\/\/|mailto:)/i.test(href) || (href.startsWith('/') && !href.startsWith('//')) ? href : null);

const Inline = ({ text }) => text.split(INLINE_RE).filter(Boolean).map((part, i) => {
  if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
  const m = part.match(LINK_RE);
  if (!m) return part;
  const href = safeHref(m[2]);
  if (!href) return m[1];
  const cls = 'font-semibold underline underline-offset-2 text-[#c2410c] hover:opacity-80';
  return href.startsWith('/')
    ? <Link key={i} to={href} className={cls}>{m[1]}</Link>
    : <a key={i} href={href} className={cls} {...(href.startsWith('mailto:') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}>{m[1]}</a>;
});

// Lines -> blocks: a heading line stands alone, consecutive "- " lines make one list,
// other consecutive lines make one paragraph; a blank line ends a block.
const toBlocks = (body) => {
  const blocks = [];
  let current = null;
  const flush = () => { if (current) { blocks.push(current); current = null; } };
  (body || '').split('\n').forEach((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); return; }
    if (line.startsWith('## ')) { flush(); blocks.push({ type: 'h3', text: line.slice(3) }); return; }
    if (line.startsWith('# ')) { flush(); blocks.push({ type: 'h2', text: line.slice(2) }); return; }
    if (line.startsWith('- ')) {
      if (current?.type !== 'ul') { flush(); current = { type: 'ul', items: [] }; }
      current.items.push(line.slice(2));
      return;
    }
    if (current?.type !== 'p') { flush(); current = { type: 'p', lines: [] }; }
    current.lines.push(line);
  });
  flush();
  return blocks;
};

const PageBody = ({ body }) => (
  <div className="space-y-4 text-base leading-relaxed" data-testid="page-body">
    {toBlocks(body).map((b, i) => {
      if (b.type === 'h2') return <h2 key={i} className="text-2xl font-bold tracking-tight pt-4 first:pt-0"><Inline text={b.text} /></h2>;
      if (b.type === 'h3') return <h3 key={i} className="text-xl font-semibold pt-2"><Inline text={b.text} /></h3>;
      if (b.type === 'ul') return <ul key={i} className="list-disc pl-6 space-y-1">{b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}</ul>;
      return <p key={i}><Inline text={b.lines.join(' ')} /></p>;
    })}
  </div>
);

export default PageBody;
